// src/pages/HrDashboard/MentoringPairsTile.js
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosInstance';

// ===================== Helper & Schwellenwerte =====================

const KICKOFF_THRESHOLD_DAYS = 14;     // ab so vielen Tagen nach Match: Kickoff fehlt
const INACTIVITY_THRESHOLD_DAYS = 14;  // ab so vielen Tagen ohne Aktivität: inaktiv
const COMPLETION_DUE_DAYS = 90;        // ab so vielen Tagen nach Match: Abschluss fällig

function fmtDate(dateString, t) {
  if (!dateString) return t('mentoringPairs.unknown', 'Unbekannt');
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return t('mentoringPairs.unknown', 'Unbekannt');
  return d.toLocaleDateString();
}

function fmtLastLogin(dateString) {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
  const formatted = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  return `${formatted} (${days}d)`;
}

function daysSince(dateString) {
  if (!dateString) return Infinity; // NULL = nie eingeloggt = maximal inaktiv
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return Infinity;
  const diffMs = Date.now() - d.getTime();
  return Math.max(0, Math.floor(diffMs / 86_400_000));
}

function coerceBool(v) {
  return v === true || v === 't' || v === 'true' || v === 1 || v === '1';
}

function getBestLastActivity(match) {
  const candidates = [
    match.last_activity_at,
    match.last_message_at,
    match.last_contact_at,
    match.last_event_at,
  ].filter(Boolean);
  const times = candidates
    .map(d => new Date(d))
    .filter(d => !Number.isNaN(d.getTime()))
    .map(d => d.getTime());
  if (times.length === 0) return null;
  return new Date(Math.max(...times)).toISOString();
}

function deriveZustand(pair) {
  if (pair.audit_typ) return 'laeuft';
  if (pair.effektiv_stufe === 3) return 'handeln';
  if (pair.manual_review_flag === true) return 'handeln';
  if (pair.effektiv_stufe === 2) return 'beobachten';
  return 'laeuft';
}

function hasOpenIssue(pair) {
  const confirmed =
    pair.confirmed_by_hr === true || pair.confirmed_by_hr === 't';
  return (
    !confirmed ||
    !pair.confirmed_by_mentee ||
    pair.kickoff_missing ||
    pair.inactive_long ||
    pair.conflict_or_unhappy ||
    pair.agreement_open ||
    pair.feedback_pending_count > 0 ||
    pair.completion_due
  );
}

// ========================== Detail-Popup ===========================

function DetailPopup({ pair, onClose }) {
  const { t } = useTranslation();
  const fmtStufe = (s) => {
    if (s === null || s === undefined) return '-';
    const wort = t(`mentoringPairs.stufeWort.${s}`, '');
    return wort ? `${s} (${wort})` : String(s);
  };
  if (!pair) return null;

  let sharedGoals = [];
  let sharedCompetences = [];
  try {
    sharedGoals = Array.isArray(pair.shared_goals)
      ? pair.shared_goals
      : JSON.parse(pair.shared_goals || '[]');
  } catch {}
  try {
    sharedCompetences = Array.isArray(pair.shared_competences)
      ? pair.shared_competences
      : JSON.parse(pair.shared_competences || '[]');
  } catch {}

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-[#232732] text-white rounded-xl shadow-xl p-8 min-w-[380px] max-w-xl relative">
        <button
          className="absolute top-2 right-2 px-2 py-1 text-white bg-gray-700 rounded hover:bg-gray-600"
          onClick={onClose}
          title={t('mentoringPairs.close', 'Schließen')}
        >
          ✕
        </button>

        <h3 className="text-lg font-bold mb-4">
          {t('mentoringPairs.popupTitle', 'Matching-Details')}
        </h3>

        <div className="flex gap-8 mb-6">
          <div className="flex flex-col items-center">
            <img
              src={
                pair.mentee_avatar_path
                  ? `${process.env.REACT_APP_IMAGE_BASE_URL}/uploads/${pair.mentee_avatar_path}`
                  : '/default-avatar.png'
              }
              alt={t('mentoringPairs.avatarAltMentee', 'Avatar Mentee')}
              className="w-20 h-20 rounded-full object-cover mb-2"
            />
            <span className="text-sm font-semibold">
              {t('mentoringPairs.mentee', 'Mentee')}
            </span>
            <span className="text-md">
              {pair.mentee_firstname} {pair.mentee_lastname}
            </span>
          </div>

          <div className="flex flex-col items-center">
            <img
              src={
                pair.mentor_avatar_path
                  ? `${process.env.REACT_APP_IMAGE_BASE_URL}/uploads/${pair.mentor_avatar_path}`
                  : '/default-avatar.png'
              }
              alt={t('mentoringPairs.avatarAltMentor', 'Avatar Mentor')}
              className="w-20 h-20 rounded-full object-cover mb-2"
            />
            <span className="text-sm font-semibold">
              {t('mentoringPairs.mentor', 'Mentor')}
            </span>
            <span className="text-md">
              {pair.mentor_firstname} {pair.mentor_lastname}
            </span>
          </div>
        </div>

        <div className="text-sm space-y-2">
          <p>
            <span className="font-semibold">{t('mentoringPairs.status', 'Status')}:</span>{' '}
            {(pair.confirmed_by_hr === true || pair.confirmed_by_hr === 't')
              ? t('mentoringPairs.confirmed', 'Bestätigt')
              : t('mentoringPairs.notConfirmed', 'Nicht bestätigt')}
          </p>
          <p>
            <span className="font-semibold">{t('mentoringPairs.matchedAt', 'Gematched am')}:</span>{' '}
            {fmtDate(pair.matched_at, t)}
          </p>
          <p>
            <span className="font-semibold">{t('mentoringPairs.score', 'Gesamtscore')}:</span>{' '}
            {pair.score ?? '-'}
          </p>
          <p>
            <span className="font-semibold">{t('mentoringPairs.fachlicherScore', 'Fachlicher Score')}:</span>{' '}
            {pair.fachlicher_score ?? '-'}
          </p>
          <p>
            <span className="font-semibold">{t('mentoringPairs.persoenlicherScore', 'Persönlicher Score')}:</span>{' '}
            {pair.persoenlicher_score ?? '-'}
          </p>
        </div>

        <div className="text-sm space-y-2 mt-3 pt-3 border-t border-gray-700/40">
          <p>
            <span className="font-semibold">{t('mentoringPairs.popupEffektivStufe', 'Effektive Stufe')}:</span>{' '}
            {fmtStufe(pair.effektiv_stufe)}
          </p>
          <p>
            <span className="font-semibold">{t('mentoringPairs.popupRawStufe', 'Plattform-Stufe')}:</span>{' '}
            {fmtStufe(pair.raw_stufe)}
          </p>
          {pair.audit_typ && (
            <p>
              <span className="font-semibold">{t('mentoringPairs.popupAuditTyp', 'Audit-Typ')}:</span>{' '}
              {t(`mentoringPairs.auditTyp.${pair.audit_typ}`, pair.audit_typ)}
            </p>
          )}
          {pair.manual_review_reason && (
            <p>
              <span className="font-semibold">{t('mentoringPairs.popupManualReason', 'Manueller Prüfgrund')}:</span>{' '}
              {pair.manual_review_reason}
            </p>
          )}
          {pair.who_is_at_zug && (
            <p>
              <span className="font-semibold">{t('mentoringPairs.popupWhoAtZug', 'Aktuell am Zug')}:</span>{' '}
              {pair.who_is_at_zug === 'mentor'
                ? t('mentoringPairs.mentor', 'Mentor')
                : pair.who_is_at_zug === 'mentee'
                  ? t('mentoringPairs.mentee', 'Mentee')
                  : pair.who_is_at_zug}
            </p>
          )}
        </div>

        <div className="mt-5">
          <h4 className="font-semibold mb-2">
            {t('mentoringPairs.sharedGoals', 'Gemeinsame Ziele')}
          </h4>
          <ul className="mb-2 list-disc list-inside">
            {sharedGoals.length === 0 && <li>{t('mentoringPairs.none', '—')}</li>}
            {sharedGoals.map((goal, i) => <li key={i}>{goal}</li>)}
          </ul>

          <h4 className="font-semibold mb-2">
            {t('mentoringPairs.sharedCompetences', 'Gemeinsame Kompetenzen')}
          </h4>
          <ul className="mb-2 list-disc list-inside">
            {sharedCompetences.length === 0 && <li>{t('mentoringPairs.none', '—')}</li>}
            {sharedCompetences.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

// =========================== Hauptkomponente =========================

export default function MentoringPairsTile({ surveyId }) {
  const { t } = useTranslation();
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPair, setSelectedPair] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [onlyNeedsAction, setOnlyNeedsAction] = useState(false);

  useEffect(() => {
    if (!surveyId) return;
    fetchPairs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  async function fetchPairs() {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/mentoring/matchings?surveyId=${surveyId}`, { withCredentials: true });
      const transformed = (Array.isArray(res?.data) ? res.data : []).map(match => {
        const daysMatch = daysSince(match.matched_at);
        const lastActIso = getBestLastActivity(match);
        const daysInactive = lastActIso ? daysSince(lastActIso) : null;

        const conflict =
          coerceBool(match.conflict) ||
          coerceBool(match.conflict_flag) ||
          coerceBool(match.has_conflict) ||
          (typeof match.satisfaction_status === 'string' &&
            ['low', 'bad', 'negative', 'unzufrieden'].includes(match.satisfaction_status.toLowerCase()));

        const completionDue =
          coerceBool(match.completion_due) ||
          (typeof daysMatch === 'number' && daysMatch >= COMPLETION_DUE_DAYS);

        const mentee_id = Number(match.mentee_id ?? match.menteeId);
        const mentor_id = Number(match.mentor_id ?? match.mentorId);

        // ✅ Kickoff ist erledigt, wenn Backend das so liefert (has_kickoff)
        const hasKickoff =
          coerceBool(match.has_kickoff) ||
          coerceBool(match.has_kickoff_appointment) ||
          coerceBool(match.has_kickoff_feedback) ||
          coerceBool(match.kickoff_done) ||
          coerceBool(match.kickoff_completed);

        // Inaktivität: basiert auf mentee_last_login / mentor_last_login
        const menteeDaysInactive = daysSince(match.mentee_last_login);
        const mentorDaysInactive = daysSince(match.mentor_last_login);
        const inactiveLong =
          menteeDaysInactive > INACTIVITY_THRESHOLD_DAYS ||
          mentorDaysInactive > INACTIVITY_THRESHOLD_DAYS;

        // Mentee-Bestätigung
        const confirmedByMentee = coerceBool(match.confirmed_by_mentee);

        // Agreement-Status
        const agreementId = match.agreement_id || null;
        const agreementMenteeConfirmed = coerceBool(match.agreement_mentee_confirmed);
        const agreementMentorConfirmed = coerceBool(match.agreement_mentor_confirmed);
        const agreementOpen = !!agreementId && (!agreementMenteeConfirmed || !agreementMentorConfirmed);

        // Feedback-Status
        const feedbackPendingCount = Number(match.feedback_pending_count) || 0;

        return {
          id: match.matching_id,
          mentee_id,
          mentor_id,
          mentee_firstname: match.mentee_firstname,
          mentee_lastname: match.mentee_lastname,
          mentee_avatar_path: match.mentee_avatar_path,
          mentor_firstname: match.mentor_firstname,
          mentor_lastname: match.mentor_lastname,
          mentor_avatar_path: match.mentor_avatar_path,
          matched_at: match.matched_at,
          status: match.status,
          score: match.score,
          fachlicher_score: match.fachlicher_score,
          persoenlicher_score: match.persoenlicher_score,
          shared_goals: match.shared_goals,
          shared_competences: match.shared_competences,
          confirmed_by_hr: match.confirmed_by_hr,
          confirmed_by_mentee: confirmedByMentee,

          // Login-Daten für Detail-Anzeige
          mentee_last_login: match.mentee_last_login,
          mentor_last_login: match.mentor_last_login,
          mentee_days_inactive: menteeDaysInactive,
          mentor_days_inactive: mentorDaysInactive,

          // Agreement
          agreement_id: agreementId,
          agreement_mentee_confirmed: agreementMenteeConfirmed,
          agreement_mentor_confirmed: agreementMentorConfirmed,
          agreement_open: agreementOpen,

          // Feedback
          feedback_pending_count: feedbackPendingCount,

          days_since_match: daysMatch,
          last_activity_at: lastActIso,
          days_inactive: daysInactive,
          kickoff_missing:
            typeof daysMatch === 'number' &&
            daysMatch >= KICKOFF_THRESHOLD_DAYS &&
            !hasKickoff,
          inactive_long: inactiveLong,
          conflict_or_unhappy: conflict,
          completion_due: completionDue,

          // P0.4b: Audit-Stufen-Daten aus VIEW match_audit_summary
          raw_stufe: typeof match.raw_stufe === 'number' ? match.raw_stufe : null,
          effektiv_stufe: typeof match.effektiv_stufe === 'number' ? match.effektiv_stufe : null,
          audit_typ: match.audit_typ || null,
          manual_review_flag: coerceBool(match.manual_review_flag),
          manual_review_reason: match.manual_review_reason || null,
          who_is_at_zug: match.who_is_at_zug || null,
          audit_agreement_status: match.audit_agreement_status || null,
        };
      });
      setPairs(transformed);
    } catch (err) {
      console.error('Fehler beim Laden der Mentoring-Paare:', err);
      setError(t('mentoringPairs.error', 'Fehler beim Laden der Mentoring-Paare.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteMatch(id) {
    if (!window.confirm(t('mentoringPairs.dissolveConfirm', 'Match wirklich auflösen?'))) return;
    try {
      await axios.patch(`/mentoring/matchings/${id}`, {}, { withCredentials: true });
      setPairs(prev => prev.filter(p => p.id !== id));
      alert(t('mentoringPairs.dissolveSuccess', 'Match wurde aufgelöst.'));
    } catch (err) {
      console.error('Fehler beim Auflösen des Matchings:', err);
      alert(t('mentoringPairs.dissolveError', 'Match konnte nicht aufgelöst werden.'));
    }
  }

  async function handleConfirmMatch(mentee_id, mentor_id) {
    try {
      await axios.patch(
        `/mentoring/matching/confirm-by-hr`,
        { menteeId: mentee_id, mentorId: mentor_id },
        { withCredentials: true }
      );
      setPairs(prev =>
        prev.map(pair =>
          pair.mentee_id === mentee_id && pair.mentor_id === mentor_id
            ? { ...pair, confirmed_by_hr: true }
            : pair
        )
      );
      alert(t('mentoringPairs.confirmSuccess', 'Match bestätigt.'));
    } catch (err) {
      console.error('Fehler beim Bestätigen:', err);
      alert(t('mentoringPairs.confirmError', 'Bestätigung fehlgeschlagen.'));
    }
  }

  async function handleConfirmAll() {
    setBulkLoading(true);
    const notConfirmed = pairs.filter(p => !(p.confirmed_by_hr === true || p.confirmed_by_hr === 't'));
    try {
      await Promise.all(
        notConfirmed.map(p =>
          axios.patch(
            `/mentoring/matching/confirm-by-hr`,
            { menteeId: p.mentee_id, mentorId: p.mentor_id },
            { withCredentials: true }
          )
        )
      );
      setPairs(prev =>
        prev.map(pair =>
          notConfirmed.some(nc => nc.mentee_id === pair.mentee_id && nc.mentor_id === pair.mentor_id)
            ? { ...pair, confirmed_by_hr: true }
            : pair
        )
      );
      alert(t('mentoringPairs.bulkConfirmSuccess', 'Alle offenen Matches bestätigt.'));
    } catch (err) {
      console.error('Fehler beim Bestätigen mehrerer Matchings:', err);
      alert(t('mentoringPairs.bulkConfirmError', 'Mehrfach-Bestätigung fehlgeschlagen.'));
    }
    setBulkLoading(false);
  }

  // ---------- Quick Chat Presets (nur Vorname) & Popup-Open ----------

  function buildPreset(kind, target, pair) {
    const firstName =
      target === 'mentee' ? (pair.mentee_firstname || '').trim() : (pair.mentor_firstname || '').trim();

    switch (kind) {
      case 'kickoff':
        return t(
          'mentoringPairs.msg.kickoff',
          'Hallo {{name}}, euer Kickoff steht noch aus. Bitte plant zeitnah euren ersten Termin (ca. 30–45 Min.). Danke!',
          { name: firstName }
        );
      case 'inactive':
        return t(
          'mentoringPairs.msg.inactive',
          'Hallo {{name}}, ich habe gesehen, dass ihr seit einiger Zeit wenig aktiv wart. Braucht ihr Unterstützung beim nächsten Schritt?',
          { name: firstName }
        );
      case 'confirm':
        return t(
          'mentoringPairs.msg.confirm',
          'Hallo {{name}}, wenn das Match für dich passt, gib mir bitte kurz ein OK – dann bestätigen wir offiziell und ihr könnt starten.',
          { name: firstName }
        );
      case 'conflict':
        return t(
          'mentoringPairs.msg.conflict',
          'Hallo {{name}}, wie läuft es im Mentoring? Wenn etwas unklar ist oder nicht passt, sag gern Bescheid – ich unterstütze euch.',
          { name: firstName }
        );
      case 'completion':
        return t(
          'mentoringPairs.msg.completion',
          'Hallo {{name}}, ihr nähert euch dem Abschluss eures Mentorings. Bitte gebt mir kurz Rückmeldung oder plant ein Abschlussgespräch.',
          { name: firstName }
        );
      case 'agreement':
        return t(
          'mentoringPairs.msg.agreement',
          'Hallo {{name}}, eure Mentoring-Vereinbarung ist noch nicht vollständig bestätigt. Bitte schau dir die Vereinbarung an und bestätige sie.',
          { name: firstName }
        );
      case 'feedback':
        return t(
          'mentoringPairs.msg.feedback',
          'Hallo {{name}}, du hast noch offenes Feedback zu deinem Mentoring. Bitte nimm dir kurz Zeit, es auszufüllen – es hilft uns, das Programm weiterzuentwickeln.',
          { name: firstName }
        );
      default:
        return '';
    }
  }

  function openPresetChat(kind, target, pair) {
    const presetMessage = buildPreset(kind, target, pair);
    const receiverRole = target; // 'mentee' | 'mentor'

    const menteeId = Number(pair.mentee_id ?? pair.menteeId);
    const mentorId = Number(pair.mentor_id ?? pair.mentorId);

    if (!Number.isFinite(menteeId) || menteeId <= 0 || !Number.isFinite(mentorId) || mentorId <= 0) {
      console.warn('[MentoringPairsTile] Fehlende IDs für Chat:', { pair, menteeId, mentorId });
      alert('Für den Chat fehlen Mentee- oder Mentor-ID. Bitte Seite neu laden oder Pair prüfen.');
      return;
    }

    console.debug('[MentoringPairsTile] openChatPopup with ids', { menteeId, mentorId, receiverRole });

    const event = new CustomEvent('openChatPopup', {
      detail: {
        menteeId,
        mentorId,
        senderRole: 'hr',
        receiverRole,
        presetMessage,
      },
    });
    window.dispatchEvent(event);
  }

  // --------------------- Counter & Filter-Logik ---------------------

  const counters = useMemo(() => {
    let laeuft = 0;
    let beobachten = 0;
    let handeln = 0;

    pairs.forEach(p => {
      const z = deriveZustand(p);
      if (z === 'laeuft') laeuft++;
      else if (z === 'beobachten') beobachten++;
      else if (z === 'handeln') handeln++;
    });

    return { laeuft, beobachten, handeln };
  }, [pairs]);

  const visiblePairs = useMemo(() => {
    const filtered = !onlyNeedsAction
      ? pairs
      : pairs.filter(p => {
          const zustand = deriveZustand(p);
          return zustand !== 'laeuft' || hasOpenIssue(p);
        });

    // P2.11: Sortierung – Zustand (handeln > beobachten > laeuft), dann manual_review_flag, dann älteste zuerst
    const zustandRang = { handeln: 0, beobachten: 1, laeuft: 2 };
    return [...filtered].sort((a, b) => {
      const rangA = zustandRang[deriveZustand(a)];
      const rangB = zustandRang[deriveZustand(b)];
      if (rangA !== rangB) return rangA - rangB;

      if (a.manual_review_flag !== b.manual_review_flag) {
        return b.manual_review_flag ? 1 : -1;
      }

      const ageA = a.days_since_match ?? 0;
      const ageB = b.days_since_match ?? 0;
      return ageB - ageA;
    });
  }, [pairs, onlyNeedsAction]);

  // ============================= Render =============================

  if (loading) {
    return (
      <div className="p-4 rounded-xl bg-white text-black shadow border border-gray-200">
        {t('mentoringPairs.loading', 'Lade Mentoring-Paare…')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-xl bg-red-100 text-red-800 shadow border border-red-300">
        {error}
      </div>
    );
  }

  const anyNotConfirmed = pairs.some(p => !(p.confirmed_by_hr === true || p.confirmed_by_hr === 't'));

  return (
    <div className="p-6 rounded-xl bg-[#2f3642] text-white shadow-md border border-[#3b434f]">
      {/* Kopfzeile */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          {t('mentoringPairs.heading', 'Mentoring-Paare')}
        </h2>
        {anyNotConfirmed && (
          <button
            onClick={handleConfirmAll}
            disabled={bulkLoading}
            className="px-5 py-2 bg-mentixxgreen text-black font-bold rounded-full hover:bg-green-400 disabled:opacity-70"
          >
            {bulkLoading
              ? t('mentoringPairs.confirming', 'Bestätige …')
              : t('mentoringPairs.confirmAll', 'Alle bestätigen')}
          </button>
        )}
      </div>

      {/* Filter-/Hinweisleiste */}
      <div className="flex flex-wrap items-center gap-3 bg-[#37404d] rounded-lg py-2 px-3 mb-4">
        <span className="text-xs bg-[#4b5563] px-3 py-1 rounded-full">
          {t('mentoringPairs.counterHandeln', 'Handeln')}: {counters.handeln}
        </span>
        <span className="text-xs bg-[#4b5563] px-3 py-1 rounded-full">
          {t('mentoringPairs.counterBeobachten', 'Beobachten')}: {counters.beobachten}
        </span>
        <span className="text-xs bg-[#4b5563] px-3 py-1 rounded-full">
          {t('mentoringPairs.counterLaeuft', 'Läuft')}: {counters.laeuft}
        </span>

        <label className="ml-3 flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!onlyNeedsAction}
            onChange={(e) => setOnlyNeedsAction(e.target.checked)}
          />
          {t('mentoringPairs.onlyNeedsAction', 'Nur Paare mit Handlungsbedarf anzeigen')}
        </label>
      </div>

      {/* Liste */}
      {visiblePairs.length === 0 ? (
        <p className="text-gray-300">{t('mentoringPairs.noPairs', 'Keine Mentoring-Paare vorhanden.')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {visiblePairs.map((pair, i) => {
            const confirmed = pair.confirmed_by_hr === true || pair.confirmed_by_hr === 't';
            const zustand = deriveZustand(pair);
            let borderClass = 'border-transparent';
            if (zustand === 'handeln') {
              borderClass = 'border-red-500/40';
            } else if (zustand === 'beobachten' || hasOpenIssue(pair)) {
              borderClass = 'border-yellow-500/40';
            }

            return (
              <div
                key={pair.id || i}
                className={`flex justify-between items-center bg-[#3b434f] p-5 rounded-lg shadow-sm border ${borderClass}`}
              >
                {/* Avatare */}
                <div className="flex items-center gap-6">
                  <div className="flex flex-col items-center">
                    <img
                      src={
                        pair.mentee_avatar_path
                          ? `${process.env.REACT_APP_IMAGE_BASE_URL}/uploads/${pair.mentee_avatar_path}`
                          : '/default-avatar.png'
                      }
                      alt={t('mentoringPairs.avatarAltMentee', 'Avatar Mentee')}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <span className="text-xs text-gray-300 mt-1">
                      {t('mentoringPairs.mentee', 'Mentee')}
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <img
                      src={
                        pair.mentor_avatar_path
                          ? `${process.env.REACT_APP_IMAGE_BASE_URL}/uploads/${pair.mentor_avatar_path}`
                          : '/default-avatar.png'
                      }
                      alt={t('mentoringPairs.avatarAltMentor', 'Avatar Mentor')}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <span className="text-xs text-gray-300 mt-1">
                      {t('mentoringPairs.mentor', 'Mentor')}
                    </span>
                  </div>
                </div>

                {/* Mitte */}
                <div className="flex-1 ml-6">
                  <div className="text-sm space-y-1">
                    <p>
                      <span className="font-semibold text-white">
                        {t('mentoringPairs.mentee', 'Mentee')}:
                      </span>{' '}
                      {pair.mentee_firstname} {pair.mentee_lastname}
                    </p>
                    <p>
                      <span className="font-semibold text-white">
                        {t('mentoringPairs.mentor', 'Mentor')}:
                      </span>{' '}
                      {pair.mentor_firstname} {pair.mentor_lastname}
                    </p>
                    <p>
                      <span className="font-semibold text-white">
                        {t('mentoringPairs.matchedAt', 'Gematched am')}:
                      </span>{' '}
                      {fmtDate(pair.matched_at, t)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t('mentoringPairs.lastLogin', 'Letzter Login')}:{' '}
                      <span className={!pair.mentee_last_login ? 'text-red-400 font-medium' : ''}>
                        {t('mentoringPairs.mentee', 'Mentee')}: {fmtLastLogin(pair.mentee_last_login) || t('mentoringPairs.noLogin', 'kein Login')}
                      </span>
                      {' | '}
                      <span className={!pair.mentor_last_login ? 'text-red-400 font-medium' : ''}>
                        {t('mentoringPairs.mentor', 'Mentor')}: {fmtLastLogin(pair.mentor_last_login) || t('mentoringPairs.noLogin', 'kein Login')}
                      </span>
                    </p>
                  </div>

                  {/* Chips */}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {zustand === 'laeuft' && (
                      <span className="bg-green-600/80 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {pair.audit_typ
                          ? t('mentoringPairs.zustand.laeuftMitTag', 'Läuft - {{tag}}', {
                              tag: t(`mentoringPairs.auditTyp.${pair.audit_typ}`, pair.audit_typ),
                            })
                          : t('mentoringPairs.zustand.laeuft', 'Läuft')}
                      </span>
                    )}

                    {zustand === 'beobachten' && (
                      <span className="bg-yellow-500/90 text-gray-900 px-3 py-1 rounded-full text-xs font-semibold">
                        {t('mentoringPairs.zustand.beobachten', 'Beobachten')}
                      </span>
                    )}

                    {zustand === 'handeln' && (
                      <>
                        <span className="bg-red-600/90 text-white px-3 py-1 rounded-full text-xs font-semibold">
                          {t('mentoringPairs.zustand.handeln', 'Handeln')}
                        </span>
                        <span className="text-xs text-gray-400">
                          {t('mentoringPairs.zustand.handelnHinweis', 'Klärung mit Mentee')}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Rechts */}
                <div className="flex flex-col gap-2 ml-4">
                  {!confirmed && (
                    <button
                      onClick={() => handleConfirmMatch(pair.mentee_id, pair.mentor_id)}
                      className="px-4 py-2 text-sm bg-mentixxgreen text-black rounded-full font-bold hover:bg-green-400"
                    >
                      {t('mentoringPairs.confirm', 'Matching bestätigen')}
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteMatch(pair.id)}
                    className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-full"
                  >
                    {t('mentoringPairs.matchDissolve', 'Match auflösen')}
                  </button>
                  <button
                    onClick={() => setSelectedPair(pair)}
                    className="px-4 py-2 text-sm text-white border border-white hover:bg-white hover:text-[#2f3642] rounded-full"
                  >
                    {t('mentoringPairs.details', 'Details anzeigen')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {selectedPair && <DetailPopup pair={selectedPair} onClose={() => setSelectedPair(null)} />}
    </div>
  );
}




